/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-duplicates */

import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Operation } from '../../services/types/Operation';
import { ToolkitTypes } from '../../services/types/Toolkit';
import { ItemMap } from '../../types';

const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';

export const toStr = (val: string | number | null) => (typeof val === 'number' ? String(val) : val);

export const dateStr = (val: string | null) =>
  val === null ? null : format(new Date(val), DEFAULT_DATE_FORMAT, { locale: enUS });

export const customDateStr = (val: string | null, customFormat = DEFAULT_DATE_FORMAT) =>
  val === null ? null : format(new Date(val), customFormat, { locale: enUS });

export const dateToCustomDateStr = (val: Date | null, customFormat = DEFAULT_DATE_FORMAT) =>
  val === null ? null : format(val, customFormat, { locale: enUS });

export const dateTimeFormat = (val: string | null) =>
  val === null ? null : format(new Date(val), "dd/MM/yyyy, H:mm'hs'");

export const capitalize = (val: string) => val.charAt(0).toUpperCase() + val.slice(1);

export const fileReader = (file: Blob) => {
  const fr = new FileReader();

  return new Promise((resolve, reject) => {
    fr.onerror = () => {
      fr.abort();
      reject(new Error('Problem parsing file'));
    };

    fr.onload = () => resolve(fr.result);
    fr.readAsText(file);
  });
};

export function findMapId<T>(array: ItemMap<T>[], mapped: T): number {
  const result = array.find((x) => x.mapped === mapped);
  if (!result) {
    return -1;
  } // the result is expected to always exist, this check is for typescript safety
  return result.id;
}

export function findMapped<T>(array: ItemMap<T>[], id: number): T {
  const result = array.find((x) => x.id === id);
  if (!result) {
    return {} as T;
  } // the result is expected to always exist, this check is for typescript safety
  return result.mapped;
}

export function safeFind<T>(array: T[], predicate: (item: T) => boolean): T {
  const result = array.find(predicate);
  if (!result) {
    return {} as T;
  } // the result is expected to always exist, this check is for typescript safety
  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function objectsAreEquals(item1: Record<string, any>, item2: Record<string, any>) {
  return !Object.keys(item1).some((key) => item1[key] !== item2[key]);
}

export const sortDate = (a: Date, b: Date) => {
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  return 0;
};

export const sortString = (a: string, b: string) => {
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  return 0;
};

export const getErrorMessage: (err: AxiosError | Error) => {
  message: string;
  reportLink: string;
} = (err) => {
  let message = null;
  let reportLink = null;
  if ('response' in err) {
    // error is AxiosError
    const serverMessage = err.response ? err.response.data.message : null;
    message = serverMessage || 'Unexpected Error';
    reportLink = err.response ? err.response.data.reportLink : undefined;
  } else {
    message = err.message;
  }
  return { message, reportLink };
};

export function genericSort<T, K extends keyof T>(
  values: T[],
  property: K,
  descending: boolean
): T[] {
  const sortedList = [...values].sort((a, b) => {
    const result = () => {
      const firstValue = a[property];
      const secondValue = b[property];

      if (property.toString() === 'modifiedOn' || property.toLocaleString() === 'createdOn') {
        const firstDate = new Date((firstValue as any).toString()).getTime();
        const secondDate = new Date((secondValue as any).toString()).getTime();

        if (firstDate > secondDate) {
          return 1;
        }
        if (firstDate < secondDate) {
          return -1;
        }

        return 0;
      }
      if (firstValue > secondValue) {
        return 1;
      }
      if (firstValue < secondValue) {
        return -1;
      }

      return 0;
    };

    return descending ? result() * -1 : result();
  });

  return sortedList;
}

export const getFlowLinkName: (flowType: string) => string = (flowType) => {
  switch (flowType.toUpperCase()) {
    case 'ESS':
      return 'ess';
    case 'ESMR':
      return 'esmr';
    case 'SR':
      return 'supervision-report';
    case 'SR_ESPF':
      return 'supervision-report-espf';
    case 'INITIAL_ESRS':
      return 'initial-esrs';
    case 'ESRS':
      return 'esrs';
    default:
      return '';
  }
};

export const getDocLinkName: (flowType: string) => string = (flowType) => {
  switch (flowType.toUpperCase()) {
    case 'ESS':
      return 'ess';
    case 'ESMR':
      return 'esmr';
    case 'SR':
      return 'supervision-report';
    case 'INITIAL_ESRS':
      return 'initial-esrs';
    case 'ESRS':
      return 'esrs';
    case 'TOOLKIT':
      return 'toolkit/list';
    case 'ESPF_TOOLKIT':
      return 'toolkit/espf/list';
    case 'ESRR':
      return 'esrr';
    case 'ACTION_PLAN':
      return 'action-plan';
    default:
      return '';
  }
};

export const getTypeLinkName = (type: string) => {
  switch (type) {
    case ToolkitTypes.Classic:
      return 'classic';
    case ToolkitTypes.FI:
      return 'fi';
    case ToolkitTypes.TCPO:
      return 'tcpo';
    case ToolkitTypes.LBR:
    case ToolkitTypes.PBL:
    case ToolkitTypes.ESPS:
      return 'other';
    default:
      return 'classic';
  }
};

export const getInitials = (fullName: string) => {
  const initialsArray = fullName.split(' ');
  const firstI = initialsArray[0][0];
  const lastI = initialsArray.length > 1 && initialsArray[1][0];
  return `${firstI}${lastI}`;
};

export const getAvatarColors = () => [
  'bg__yellow',
  'bg__orange',
  'bg__red',
  'bg__light_green',
  'bg__dark_green',
  'bg__purple',
  'bg__brown',
  'bg__warm_gray',
  'bg__cold_dark_gray',
  'bg__dark_cyan',
];

export const isDraft = (op: Operation) => op && op.number.startsWith('DF-');

// Modifier de Popper.js (via reactstrap's UncontrolledTooltip/UncontrolledPopover) para
// evitar que el tooltip se recorte contra el viewport. Los tipos de reactstrap declaran
// `modifiers?: Modifier<string, any>[]` (Modifier requiere enabled/phase/fn, que Popper.js
// completa en runtime al mergear con sus defaults - un partial override como este es el uso
// estandar documentado por Popper.js, pero no hay forma de tipar ese partial sin `any` dado
// como reactstrap expone el tipo). Constante compartida en vez de repetir el mismo cast en
// cada tooltip (65 ocurrencias idénticas antes de esta fase).
//
// `offset`: Bootstrap 5 dibuja la flecha (.tooltip-arrow) por FUERA de la caja del tooltip
// (`top: calc(-1 * var(--bs-tooltip-arrow-height))`, 0.4rem), y reactstrap 9 posiciona con
// `offset: [0, 0]` por default, así que la caja quedaba pegada al elemento y la flecha se
// montaba encima de él. Bootstrap 5 usa [0, 6] en su propio JS (js/src/tooltip.js), pero con
// la flecha de 0.4rem (6.4px) la punta todavía pisaba ~0.6px del elemento: con el mouse en ese
// borde el cursor quedaba sobre el tooltip, el elemento recibía mouseout, el tooltip se cerraba
// y volvía a abrir (flicker). 8 deja la punta ~1.6px por debajo del borde (mismo valor que
// Bootstrap 5 usa para los popovers). Al pasar un modifier `offset` acá, reactstrap descarta el suyo.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tooltipPreventOverflowModifiers: any = [
  { name: 'preventOverflow', options: { rootBoundary: 'viewport' } },
  { name: 'offset', options: { offset: [0, 8] } },
];
